import { VisuallyHidden } from '@/components/ui/VisuallyHidden';
import { useT } from '@/lib/i18n';
import type { SocialLink } from './social';

/**
 * The social networks as their logos: round 44 px targets (the touch minimum), each a link that opens in a new
 * tab and is named for a screen reader — the logo is decoration. The logo files are static (`public/social`),
 * so they cost the page no JavaScript; they load lazily, as the links sit low on the page. Styles:
 * `styles/components/footer.css`.
 */
export function SocialLinks({
  links,
  location,
}: {
  links: readonly SocialLink[];
  /** A map pin after the logos (D-329): where the firm is — the address, and the link that opens it in Google Maps. */
  location?: { href: string; address: string; label: string } | null;
}) {
  const t = useT();
  return (
    <ul className="social-links">
      {links.map(({ key, name, logo, href }) => (
        <li key={key}>
          <a href={href} target="_blank" rel="noopener noreferrer" className="social-link">
            <img src={logo} width={24} height={24} alt="" loading="lazy" decoding="async" />
            <VisuallyHidden>
              {name} {t('common.opensInNewTab')}
            </VisuallyHidden>
          </a>
        </li>
      ))}
      {location ? (
        <li>
          <a
            href={location.href}
            target="_blank"
            rel="noopener noreferrer"
            className="social-link social-link--pin"
            title={location.address}
            aria-label={`${location.label}: ${location.address} ${t('common.opensInNewTab')}`}
          >
            <svg
              viewBox="0 0 24 24"
              width="22"
              height="22"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11Z" />
              <circle cx="12" cy="10" r="2.4" />
            </svg>
          </a>
        </li>
      ) : null}
    </ul>
  );
}
